import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { toSlug, find, isWorkspaceAdmin } from '../../utils/helpers';

import { openModal } from '../../redux/Slices/modalSlice';
import { config } from '../../config';

import CreateWorkspace from './CreateWorkspace';

import { fetchWorkspaces, updateWorkspace } from '../../redux/Slices/workspaceSlice';
import {
    fetchBoards,
    fetchArchivedBoards,
    clearBoardsCache,
    optimisticUpdateBoardInLists,
    optimisticAddBoardToLists,
    optimisticRemoveBoardFromLists,
    optimisticRemoveFromArchivedBoards
} from '../../redux/Slices/boardsSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';
import { api } from '../../utils/apiUtils';

// import WorkspaceSidebar from './WorkspaceSidebar';

import './scss/workspace.scss';
import './scss/Boards.scss';

import BoardSection from './BoardSection';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';

const Boards = () => {
    const { workspaces } = useSelector(state => state.workspaces);
    const { shortId } = useParams();
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { archivedBoards, archivedStatus } = useSelector(state => state.boards);

    // Find the current workspace based on the shortId
    const workspace = find.get(workspaces, { name: 'workspace', workspace: shortId });

    // Workspace editing state
    const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);
    const [workspaceName, setWorkspaceName] = useState('');
    const [workspaceUpdateStatus, setWorkspaceUpdateStatus] = useState('');
    const [showArchived, setShowArchived] = useState(true);
    const [archivedLimit, setArchivedLimit] = useState(5);
    const [deletingBoardId, setDeletingBoardId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [unarchivingBoardId, setUnarchivingBoardId] = useState(null);

    // Get workspace and check if current user is admin
    const currentUserId = user?.user?._id;
    const currentUserIsAdmin = isWorkspaceAdmin(workspace, currentUserId);


    // Dispatch fetchWorkspaces on component mount
    useEffect(() => {
        dispatch(fetchWorkspaces());
    }, [dispatch]);

    // Fetch boards for the specific workspace when it changes
    useEffect(() => {
        if (shortId) {
            dispatch(clearBoardsCache());
            dispatch(fetchBoards({ workspaceId: shortId, useCache: false }));
        }
    }, [dispatch, shortId]);

    // Fetch archived boards when the section is expanded
    useEffect(() => {
        if (showArchived && shortId) {
            dispatch(fetchArchivedBoards({ workspaceId: shortId }));
        }
    }, [dispatch, showArchived, shortId]);

    const handleUnarchiveBoard = async (boardId) => {
        if (unarchivingBoardId) return;
        setUnarchivingBoardId(boardId);

        try {
            await api.post(`${config.API_URI}/v1/boards/archive/${boardId}`, { archived: false });
            dispatch(optimisticRemoveFromArchivedBoards(boardId));
            // Refresh workspace data so BoardSection picks up the restored board
            dispatch(fetchWorkspaces());
            showSuccess(dispatch, 'Board restored');
        } catch (error) {
            console.error('Error unarchiving board:', error);
            showError(dispatch, 'Failed to restore board');
        } finally {
            setUnarchivingBoardId(null);
        }
    };

    const handleDeleteArchivedBoard = async (boardId) => {
        if (deletingBoardId) return;
        setDeletingBoardId(boardId);

        try {
            await api.post(`${config.API_URI}/v1/boards/delete/${boardId}`);
            dispatch(optimisticRemoveFromArchivedBoards(boardId));
            showSuccess(dispatch, 'Board permanently deleted');
            setConfirmDeleteId(null);
        } catch (error) {
            console.error('Error deleting board:', error);
            showError(dispatch, 'Failed to delete board');
        } finally {
            setDeletingBoardId(null);
        }
    };

    // Update workspace name when workspace changes
    useEffect(() => {
        if (workspace) {
            setWorkspaceName(workspace.name);
        }
    }, [workspace]);

    // Real-time socket listener for board changes
    useSocketAction([
        ActionTypes.BOARD_CREATED,
        ActionTypes.BOARD_UPDATED,
        ActionTypes.BOARD_DELETED,
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
    ], (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        console.log('[Boards] Real-time board event:', data.action, data);

        switch (data.action) {
            case ActionTypes.BOARD_CREATED:
                // ✅ Add board to boards list using optimistic action
                if (data.board) {
                    dispatch(optimisticAddBoardToLists(data.board));
                }
                break;

            case ActionTypes.BOARD_UPDATED:
                // ✅ Update board in boards list using optimistic action
                if (data.boardId && (data.board || data.updatedFields)) {
                    const updates = data.board || data.updatedFields;
                    dispatch(optimisticUpdateBoardInLists({
                        boardId: data.boardId,
                        updates
                    }));
                }
                break;

            case ActionTypes.BOARD_DELETED:
                // ✅ Remove board from boards list using optimistic action
                if (data.boardId) {
                    dispatch(optimisticRemoveBoardFromLists(data.boardId));
                }
                break;

            case ActionTypes.MEMBER_ADDED:
                // ✅ Board member added - if current user was added, show the board
                if (data.boardId && data.board) {
                    const isCurrentUserAdded = data.newMembers?.some(
                        nm => nm.user.toString() === user?.user?._id
                    );

                    if (isCurrentUserAdded) {
                        // Add the board to the user's board lists
                        dispatch(optimisticAddBoardToLists(data.board));
                    } else {
                        // Update board members in existing board
                        dispatch(optimisticUpdateBoardInLists({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            case ActionTypes.MEMBER_REMOVED:
                // ✅ Board member removed - if current user was removed, hide the board
                if (data.boardId && data.memberId) {
                    if (data.memberId === user?.user?._id) {
                        // Remove the board from the user's board lists
                        dispatch(optimisticRemoveBoardFromLists(data.boardId));
                    } else {
                        // Update board members in existing board
                        dispatch(optimisticUpdateBoardInLists({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            default:
                break;
        }
    }, [dispatch, user?.user?._id]);

    const handleCreateWorkspace = () => {
        dispatch(openModal({ modalType: 'CREATE_WORKSPACE', modalData: {} }));
    };

    const [openWorkspaces, setOpenWorkspaces] = useState({});

    useEffect(() => {
        const initialOpenWorkspaces = {};
        workspaces.slice(0, 2).forEach(workspace => {
            initialOpenWorkspaces[workspace.name] = true;
        });
        setOpenWorkspaces(initialOpenWorkspaces);
    }, [workspaces]);

    const toggleOptions = (workspaceName) => {
        setOpenWorkspaces(prevState => ({
            ...prevState,
            [workspaceName]: !prevState[workspaceName]
        }));
    };

    // Workspace name update handlers
    const handleEditWorkspaceName = () => {
        if (workspace) {
            setIsEditingWorkspaceName(true);
        }
    };

    const handleUpdateWorkspaceName = async () => {
        if (!workspace || !workspaceName.trim() || workspaceName.trim() === workspace.name) {
            setIsEditingWorkspaceName(false);
            setWorkspaceName(workspace?.name || '');
            return;
        }

        try {
            setWorkspaceUpdateStatus('saving');
            await dispatch(updateWorkspace({
                workspaceId: workspace.shortId,
                name: workspaceName.trim()
            })).unwrap();

            setWorkspaceUpdateStatus('saved');
            setIsEditingWorkspaceName(false);
            dispatch(showSnackBar({ message: 'Workspace name updated successfully', type: 'success' }));

            // Clear saved status after 2 seconds
            setTimeout(() => setWorkspaceUpdateStatus(''), 2000);
        } catch (error) {
            console.error('Error updating workspace name:', error);
            setWorkspaceUpdateStatus('error');
            dispatch(showSnackBar({ message: 'Failed to update workspace name', type: 'error' }));

            // Reset to original name on error
            setWorkspaceName(workspace?.name || '');
            setIsEditingWorkspaceName(false);

            // Clear error status after 3 seconds
            setTimeout(() => setWorkspaceUpdateStatus(''), 3000);
        }
    };

    const handleWorkspaceNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleUpdateWorkspaceName();
        } else if (e.key === 'Escape') {
            setIsEditingWorkspaceName(false);
            setWorkspaceName(workspace?.name || '');
        }
    };

    return (
        <div className="zppbbe-workspace-wrapper">
            <div className="zoobbe-workspace-container">

                <div className='zoobbe-main-content-container'>
                    <div className="zoobbe-main-content">
                        {workspace ? (
                            <>
                                <div className="board-header">
                                    <div className="board-icon">
                                        <span className="icon-text">{workspaceName?.charAt(0)?.toUpperCase() || 'W'}</span>
                                    </div>
                                    <div className="board-details">
                                        <div className="board-name">
                                            {isEditingWorkspaceName ? (
                                                <input
                                                    type="text"
                                                    value={workspaceName}
                                                    onChange={(e) => setWorkspaceName(e.target.value)}
                                                    onBlur={handleUpdateWorkspaceName}
                                                    onKeyDown={handleWorkspaceNameKeyDown}
                                                    className="workspace-name-input"
                                                    autoFocus
                                                    maxLength={50}
                                                />
                                            ) : (
                                                <>
                                                    <span>{workspaceName}</span>
                                                    {currentUserIsAdmin && (
                                                        <span
                                                            className="material-symbols-outlined edit-icon"
                                                            onClick={handleEditWorkspaceName}
                                                            title="Edit workspace name"
                                                        >
                                                            edit
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                            {workspaceUpdateStatus && (
                                                <div className="workspace-update-status">
                                                    {workspaceUpdateStatus === 'saving' && (
                                                        <span className="save-status saving">
                                                            <span className="material-symbols-outlined">sync</span>
                                                        </span>
                                                    )}
                                                    {workspaceUpdateStatus === 'saved' && (
                                                        <span className="save-status saved">
                                                            <CircleCheckOutlined />
                                                        </span>
                                                    )}
                                                    {workspaceUpdateStatus === 'error' && (
                                                        <span className="save-status error">
                                                            <span className="material-symbols-outlined">error</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="board-meta-row">
                                            <span className="meta-tag">
                                                <span className="material-symbols-outlined">lock</span>
                                                Private
                                            </span>
                                            {workspace.members && (
                                                <span className="meta-tag">
                                                    <span className="material-symbols-outlined">group</span>
                                                    {workspace.members.length} {workspace.members.length === 1 ? 'member' : 'members'}
                                                </span>
                                            )}
                                            {workspace.boards && (
                                                <span className="meta-tag">
                                                    <span className="material-symbols-outlined">dashboard</span>
                                                    {workspace.boards.length} {workspace.boards.length === 1 ? 'board' : 'boards'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <BoardSection workspace={workspace} defaultSelectedOption={0} />

                                {/* Archived boards section */}
                                {currentUserIsAdmin && (
                                    <div className="archived-boards-section">
                                        <div
                                            className="archived-header"
                                            onClick={() => setShowArchived(!showArchived)}
                                        >
                                            <div className="archived-header-left">
                                                <span className="material-symbols-outlined archived-chevron">
                                                    {showArchived ? 'expand_more' : 'chevron_right'}
                                                </span>
                                                <span className="material-symbols-outlined archived-icon">inventory_2</span>
                                                <h4 className="header-title">Archived boards</h4>
                                                {archivedBoards.length > 0 && (
                                                    <span className="archived-count">{archivedBoards.length}</span>
                                                )}
                                            </div>
                                        </div>

                                        {showArchived && (
                                            <div className="archived-boards-list">
                                                {archivedStatus === 'loading' && (
                                                    <div className="archived-empty-state">
                                                        <span className="material-symbols-outlined spinning">sync</span>
                                                        <span>Loading archived boards...</span>
                                                    </div>
                                                )}
                                                {archivedStatus === 'succeeded' && archivedBoards.length === 0 && (
                                                    <div className="archived-empty-state">
                                                        <span className="material-symbols-outlined">inventory_2</span>
                                                        <span>No archived boards</span>
                                                    </div>
                                                )}
                                                {archivedBoards.slice(0, archivedLimit).map(board => {
                                                    const boardMembers = (board.members || []).filter(m => m._id);
                                                    const boardCards = board.cardsCount || 0;

                                                    return (
                                                        <div key={board._id} className="archived-board-item">
                                                            <div className="archived-board-info">
                                                                <div
                                                                    className="archived-board-cover"
                                                                    style={
                                                                        board?.cover?.sizes?.medium || board?.cover?.url
                                                                            ? { backgroundImage: `url(${board.cover.sizes?.medium || board.cover.url})` }
                                                                            : board?.cover?.coverColor?.[2]
                                                                                ? { background: board.cover.coverColor[2] }
                                                                                : {}
                                                                    }
                                                                />
                                                                <div className="archived-board-details">
                                                                    <span className="archived-board-title">{board.title}</span>
                                                                    <span className="archived-board-meta">
                                                                        {boardCards} {boardCards === 1 ? 'card' : 'cards'}
                                                                        {boardMembers.length > 0 && (
                                                                            <> &middot; {boardMembers.length} {boardMembers.length === 1 ? 'member' : 'members'}</>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="archived-board-actions">
                                                                {confirmDeleteId === board.shortId ? (
                                                                    <>
                                                                        <button
                                                                            className="archived-btn delete-confirm"
                                                                            onClick={() => handleDeleteArchivedBoard(board.shortId)}
                                                                            disabled={deletingBoardId === board.shortId}
                                                                        >
                                                                            <span className="material-symbols-outlined">warning</span>
                                                                            {deletingBoardId === board.shortId ? 'Deleting...' : 'Confirm delete'}
                                                                        </button>
                                                                        <button
                                                                            className="archived-btn cancel"
                                                                            onClick={() => setConfirmDeleteId(null)}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <button
                                                                            className="archived-btn restore"
                                                                            onClick={() => handleUnarchiveBoard(board.shortId)}
                                                                            disabled={unarchivingBoardId === board.shortId}
                                                                        >
                                                                            <span className="material-symbols-outlined">unarchive</span>
                                                                            {unarchivingBoardId === board.shortId ? 'Restoring...' : 'Restore'}
                                                                        </button>
                                                                        <button
                                                                            className="archived-btn delete"
                                                                            onClick={() => setConfirmDeleteId(board.shortId)}
                                                                        >
                                                                            <span className="material-symbols-outlined">delete_outline</span>
                                                                            Delete
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {archivedBoards.length > archivedLimit && (
                                                    <button
                                                        className="archived-show-more"
                                                        onClick={() => setArchivedLimit(prev => prev + 10)}
                                                    >
                                                        Show more ({archivedBoards.length - archivedLimit} remaining)
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <p>Loading workspace...</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Boards;
